import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Category } from 'models/categories';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { TreeSelect } from 'components/treeselect/TreeSelect';
import { TreeItemWithIcon } from 'components/treeselect/CustomTreeItem';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';

interface CategorySelectProps {
  selectedCategories?: string[];
  setSelectedCategories?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}

export function CategorySelect({
  selectedCategories = undefined,
  setSelectedCategories = undefined,
  className = undefined,
}: CategorySelectProps) {
  const intl = useIntl();

  const { allCategories } = useTransitiveCacheStore();
  const { categories, setCategories } = useAppStore();

  const { disabledFilters } = useTransitiveCacheStore();

  const sortItemLevel = (treeItems: TreeViewBaseItem<TreeItemWithIcon>[]) =>
    treeItems.sort((a, b) => a.label.localeCompare(b.label));

  const categoryToTreeViewBaseItem = (category: Category): TreeViewBaseItem<TreeItemWithIcon> => {
    // If we have exactly one child, let's not show the parent at all
    const cat = category.subCategories && category.subCategories.length === 1 ? category.subCategories[0] : category;
    return {
      id: cat.categoryCode,
      label: intl.formatMessage({
        id: `product.categories.${cat.categoryCode}`,
        defaultMessage: cat.categoryCode,
      }),
      icon: findIconForCategory(cat.categoryCode, 20),
      children: sortItemLevel(cat.subCategories ? cat.subCategories.map(c => categoryToTreeViewBaseItem(c)) : []),
    };
  };

  const allOptions = useMemo(() => sortItemLevel(allCategories.map(categoryToTreeViewBaseItem)), [allCategories]);

  return (
    <TreeSelect
      disabled={disabledFilters.includes('prdCategory')}
      id="category-filter"
      className={className}
      label={intl.formatMessage({
        id: 'filter.key.selectedCategories',
        defaultMessage: 'Categories',
      })}
      items={allOptions}
      selectedItems={selectedCategories ?? categories ?? []}
      setSelectedItems={setSelectedCategories ?? setCategories}
      showConfirmButton
    />
  );
}
